// frontend/app/components/discoveryJourneys/DiscoveryJourneyMap.js
//
// The Discovery Journey screen: the ASCII tree from the Saras Learning Loop
// document, drawn.
//
//   Chapter 2: Can you design the fastest Rescue Drone?
//   |--- Learning Loop 1 · How Fast Are We Moving?
//   |--- Learning Loop 2 · Why Isn't Speed Enough?
//   |__  ...
//
// Two things make this a story rather than a list of lessons, and both are
// authored data, never invented here:
//   * the mission header — the compelling question, the set-up, what success
//     looks like — which is why a student is in this chapter at all
//     (JourneyMissionHeader);
//   * the bridge prompt hanging off each loop, the question that makes the next
//     loop necessary, "Why isn't speed enough?" (JourneyBridge).
//
// This file is the composition: states, the chain, and the footer slot the
// screen drops JourneyMasteryMap into. Props-only — everything arrives from the
// runtime payload the screen fetched, so it renders without a network.

import React from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getLessonColors } from "../lessonPlans/lessonPlanTheme";
import { journeyProgress } from "./journeyProgress";
import JourneyMissionHeader from "./JourneyMissionHeader";
import { JourneyBridge, JourneyLoopRow } from "./JourneyLoopRow";

const NOOP = () => {};

const DiscoveryJourneyMap = ({
  // The runtime payload itself. It is flat — `title`, `mission_story`,
  // `objectives`, `success_criteria` and `loops` all sit at the top level,
  // mirroring services/lesson_runtime_builder.build_runtime_payload — so
  // `<DiscoveryJourneyMap journey={runtime} />` is the whole call.
  journey = null,
  // Only needed to render a chain other than `journey.loops`.
  loops = null,
  // Wired to the lesson runtime by the host screen. A no-op default keeps the
  // map renderable — and pressable — before anything is wired.
  onOpenLoop = NOOP,
  loading = false,
  error = null,
  isDarkMode = false,
  // Rendered under the chain — the journey screen puts JourneyMasteryMap here.
  footer = null,
  style,
  t = (s) => s,
}) => {
  const colors = getLessonColors(isDarkMode);
  // An explicit `loops` wins, including an explicit empty one — a caller saying
  // "this journey has no loops" is not the same as not saying anything.
  const list = Array.isArray(loops)
    ? loops
    : Array.isArray(journey?.loops)
      ? journey.loops
      : [];
  const progress = journeyProgress(list);

  if (loading) {
    return (
      <View testID="journey-map" style={[styles.stateBox, style]}>
        <ActivityIndicator size="small" color={colors.accentPrimary} />
        <Text style={[styles.stateText, { color: colors.textMuted }]}>
          {t("Loading this journey…")}
        </Text>
      </View>
    );
  }

  if (error || !journey) {
    return (
      <View
        testID="journey-map"
        style={[styles.stateBox, styles.stateBoxBordered, { borderColor: colors.border }, style]}
      >
        <MaterialCommunityIcons name="alert-outline" size={16} color={colors.textMuted} />
        <Text style={[styles.stateText, { color: colors.textMuted }]}>
          {t("This journey could not be loaded.")}
        </Text>
      </View>
    );
  }

  return (
    <View testID="journey-map" style={style}>
      <JourneyMissionHeader
        journey={journey}
        progress={progress}
        colors={colors}
        isDarkMode={isDarkMode}
        t={t}
      />

      {list.length === 0 ? (
        <View
          testID="journey-empty"
          style={[styles.stateBox, styles.stateBoxBordered, { borderColor: colors.border }]}
        >
          <MaterialCommunityIcons name="map-outline" size={16} color={colors.textMuted} />
          <Text style={[styles.stateText, { color: colors.textMuted }]}>
            {t("No learning loops have been added to this journey yet.")}
          </Text>
        </View>
      ) : (
        <View testID="journey-chain" style={styles.chain}>
          {list.map((loop, index) => (
            <React.Fragment key={loop?.id ?? `${index}-${loop?.lesson_plan_id ?? "none"}`}>
              <JourneyLoopRow
                loop={loop}
                index={index}
                // The rail keeps running while a bridge still hangs below it.
                isLast={index === list.length - 1 && !loop?.bridge_prompt}
                colors={colors}
                isDarkMode={isDarkMode}
                onOpenLoop={onOpenLoop || NOOP}
                t={t}
              />
              <JourneyBridge
                prompt={loop?.bridge_prompt}
                nextLoop={list[index + 1]}
                index={Number(loop?.sequence) || index + 1}
                colors={colors}
                t={t}
              />
            </React.Fragment>
          ))}
        </View>
      )}

      {footer}
    </View>
  );
};

const styles = StyleSheet.create({
  stateBox: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    paddingHorizontal: 12,
    gap: 8,
  },
  stateBoxBordered: { borderWidth: 1, borderRadius: 12 },
  stateText: { fontSize: 13, marginLeft: 8 },
  chain: {},
});

export default DiscoveryJourneyMap;
